import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Package,
  ArrowRightLeft,
  Plus,
  Trash2,
  Loader2,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Scale,
} from 'lucide-react';
import { toast } from 'sonner';
import complianceService from '../services/complianceService';

const MOVEMENT_TYPES = ['In', 'Out', 'Adjustment', 'Waste', 'Transfer'];

const movementBadge = (type) => {
  const t = type?.toLowerCase();
  if (t === 'in') return <Badge className="bg-green-100 text-green-800">{type}</Badge>;
  if (t === 'out') return <Badge className="bg-red-100 text-red-800">{type}</Badge>;
  if (t === 'waste') return <Badge className="bg-orange-100 text-orange-800">{type}</Badge>;
  if (t === 'transfer') return <Badge className="bg-blue-100 text-blue-800">{type}</Badge>;
  return <Badge variant="secondary">{type || 'N/A'}</Badge>;
};

export default function InventoryManagementPage() {
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState(null);

  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState('');

  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [isLoadingMovements, setIsLoadingMovements] = useState(false);
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [newMovement, setNewMovement] = useState({
    movementType: 'In',
    quantityChange: '',
    reason: '',
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedProductId) {
      setSelectedBatchId('');
      setBatches([]);
      setMovements([]);
      setSummary(null);
      fetchBatches(selectedProductId);
    }
  }, [selectedProductId]);

  useEffect(() => {
    if (selectedBatchId) {
      fetchBatchData(selectedBatchId);
    } else {
      setMovements([]);
      setSummary(null);
    }
  }, [selectedBatchId]);

  const fetchProducts = async () => {
    setIsLoadingProducts(true);
    try {
      const data = await complianceService.getProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const fetchBatches = async (productId) => {
    setIsLoadingBatches(true);
    try {
      const data = await complianceService.getBatches(productId);
      setBatches(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load batches');
      setBatches([]);
    } finally {
      setIsLoadingBatches(false);
    }
  };

  const fetchBatchData = async (batchId) => {
    setIsLoadingMovements(true);
    try {
      const [movData, sumData] = await Promise.allSettled([
        complianceService.getInventoryByBatch(batchId),
        complianceService.getInventoryBatchSummary(batchId),
      ]);
      if (movData.status === 'fulfilled') setMovements(Array.isArray(movData.value) ? movData.value : []);
      if (sumData.status === 'fulfilled') setSummary(sumData.value);
    } catch {
      toast.error('Failed to load inventory data');
    } finally {
      setIsLoadingMovements(false);
    }
  };

  const handleLogMovement = async (e) => {
    e.preventDefault();
    if (!selectedBatchId) return;
    setIsSaving(true);
    try {
      await complianceService.logInventoryMovement({
        batchId: Number(selectedBatchId),
        movementType: newMovement.movementType,
        quantityChange: Number(newMovement.quantityChange),
        reason: newMovement.reason || null,
      });
      toast.success('Movement logged successfully');
      setIsLogDialogOpen(false);
      setNewMovement({ movementType: 'In', quantityChange: '', reason: '' });
      fetchBatchData(selectedBatchId);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to log movement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteMovement = async (id) => {
    if (!window.confirm('Delete this movement record?')) return;
    try {
      await complianceService.deleteInventoryMovement(id);
      toast.success('Movement deleted');
      fetchBatchData(selectedBatchId);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to delete movement');
    }
  };

  const selectedProduct = products.find(p => String(p.productId ?? p.id) === String(selectedProductId));
  const selectedBatch = batches.find(b => String(b.batchId) === String(selectedBatchId));

  const totalIn = movements
    .filter(m => m.movementType?.toLowerCase() === 'in')
    .reduce((s, m) => s + (m.quantityChange ?? 0), 0);
  const totalOut = movements
    .filter(m => ['out', 'waste', 'transfer'].includes(m.movementType?.toLowerCase()))
    .reduce((s, m) => s + Math.abs(m.quantityChange ?? 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Inventory Management</h1>
            <p className="text-muted-foreground mt-1">Track inventory movements by product batch</p>
          </div>
          <div className="flex gap-2">
            {selectedBatchId && (
              <Button variant="outline" size="sm" onClick={() => fetchBatchData(selectedBatchId)}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            )}
            <Button onClick={() => setIsLogDialogOpen(true)} disabled={!selectedBatchId}>
              <Plus className="h-4 w-4 mr-2" />
              Log Movement
            </Button>
          </div>
        </div>

        {/* Selectors */}
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Product</Label>
                {isLoadingProducts ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading products...
                  </div>
                ) : (
                  <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.productId ?? p.id} value={String(p.productId ?? p.id)}>
                          {p.productName ?? p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label>Batch</Label>
                {isLoadingBatches ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading batches...
                  </div>
                ) : (
                  <Select
                    value={selectedBatchId}
                    onValueChange={setSelectedBatchId}
                    disabled={!selectedProductId || batches.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          !selectedProductId
                            ? 'Select a product first'
                            : batches.length === 0
                            ? 'No batches found'
                            : 'Select a batch'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.map(b => (
                        <SelectItem key={b.batchId} value={String(b.batchId)}>
                          {b.batchNumber} — {b.batchStatus}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Batch selected — show data */}
        {selectedBatchId && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Current Quantity</p>
                      <p className="text-3xl font-bold mt-1">
                        {summary != null ? summary.currentQuantity : '—'}
                      </p>
                      {summary?.unitOfMeasure && (
                        <p className="text-xs text-muted-foreground mt-1">{summary.unitOfMeasure}</p>
                      )}
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <Scale className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Received</p>
                      <p className="text-3xl font-bold mt-1 text-green-600">+{totalIn}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Removed</p>
                      <p className="text-3xl font-bold mt-1 text-red-600">-{totalOut}</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg">
                      <TrendingDown className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Batch Status</p>
                      <p className="text-lg font-semibold mt-1">{selectedBatch?.batchStatus ?? 'N/A'}</p>
                      <p className="text-xs text-muted-foreground">{selectedBatch?.stateCode}</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <Package className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Movements Table */}
            <Card>
              <CardHeader>
                <CardTitle>Movement History</CardTitle>
                <CardDescription>
                  Batch: {selectedBatch?.batchNumber} — {selectedProduct?.productName ?? selectedProduct?.name}
                  {' '}({movements.length} record{movements.length !== 1 ? 's' : ''})
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingMovements ? (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : movements.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ArrowRightLeft className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No movements logged yet. Use "Log Movement" to record the first transaction.</p>
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Qty Change</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {movements.map((m) => (
                          <TableRow key={m.inventoryMovementId}>
                            <TableCell className="text-sm text-muted-foreground">
                              {m.movementDate ? new Date(m.movementDate).toLocaleString() : 'N/A'}
                            </TableCell>
                            <TableCell>{movementBadge(m.movementType)}</TableCell>
                            <TableCell
                              className={`font-medium ${m.quantityChange >= 0 ? 'text-green-600' : 'text-red-600'}`}
                            >
                              {m.quantityChange >= 0 ? '+' : ''}{m.quantityChange}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {m.reason || '—'}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteMovement(m.inventoryMovementId)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Empty state */}
        {!selectedProductId && !isLoadingProducts && (
          <div className="text-center py-20 text-muted-foreground">
            <Package className="h-16 w-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg">Select a product to view its inventory</p>
          </div>
        )}
      </div>

      {/* Log Movement Dialog */}
      <Dialog open={isLogDialogOpen} onOpenChange={setIsLogDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Inventory Movement</DialogTitle>
            <DialogDescription>
              Record a stock change for batch <strong>{selectedBatch?.batchNumber}</strong>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogMovement} className="space-y-4">
            <div className="space-y-1">
              <Label>Movement Type *</Label>
              <Select
                value={newMovement.movementType}
                onValueChange={v => setNewMovement(p => ({ ...p, movementType: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOVEMENT_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Quantity Change *</Label>
              <Input
                type="number"
                required
                placeholder="e.g. 100 or -50"
                value={newMovement.quantityChange}
                onChange={e => setNewMovement(p => ({ ...p, quantityChange: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Use positive for additions, negative for reductions
              </p>
            </div>

            <div className="space-y-1">
              <Label>Reason</Label>
              <Input
                placeholder="Optional reason or note"
                value={newMovement.reason}
                onChange={e => setNewMovement(p => ({ ...p, reason: e.target.value }))}
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1" disabled={isSaving}>
                {isSaving
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <ArrowRightLeft className="h-4 w-4 mr-2" />}
                Log Movement
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsLogDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
